/*
 * WaWiAppQueries.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-04 in huWaWi3 33.4.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { gql } from 'graphql-tag'

import {
  AUFTRAGSENTWURF_FRAGMENT,
  AUFTRAG_FRAGMENT,
  PREIS_FRAGMENT,
  QX_PREIS_FRAGMENT,
  RESERVIERUNGSPOSITION_FRAGMENT,
  TEMPLATECONFIG_FRAGMENT,
  VERSANDANWEISUNG_FRAGMENT,
} from '../fragments'

export const AUFTRAG_GESPERRT_LIST_QUERY = gql`
  query AlleAuftraegeGesperrt($cursor: String) {
    nodes: hwAuftragPages(
      after: $cursor
      filter: { erledigt: { eq: false }, zuteilungssperre: { eq: true } }
    ) {
      edges {
        cursor
        node {
          ...auftragFragment
          kunde {
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${AUFTRAG_FRAGMENT}
`
export const RESERVIERUNGSPOS_LIST_QUERY = gql`
  query ReservierungsPositionen($cursor: String) {
    nodes: hwReservierungsPositionPages(after: $cursor, filter: { erledigt: { eq: false } }) {
      edges {
        cursor
        node {
          ...resevierungspositionFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${RESERVIERUNGSPOSITION_FRAGMENT}
`
export const VA_LIST_QUERY = gql`
  query Versandanweisungen($cursor: String) {
    nodes: hwVersandanweisungPages(after: $cursor) {
      edges {
        cursor
        node {
          ...versandanweisungFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${VERSANDANWEISUNG_FRAGMENT}
`

export const VA_ENTITY_QUERY = gql`
  query Versandanweisung($nr: ID!) {
    entity: hwVersandanweisung(designator: $nr) {
      ...versandanweisungFragment
    }
  }
  ${VERSANDANWEISUNG_FRAGMENT}
`

export const VA_CREATE_MUTATION = gql`
  mutation VersandanweisungZufuegen($designator: ID!, $input: HwVersandanweisungInput!) {
    entity: hwVersandanweisungCreate(designator: $designator, input: $input) {
      ...versandanweisungFragment
    }
  }
  ${VERSANDANWEISUNG_FRAGMENT}
`

export const QX_LIST_QUERY = gql`
  query QxPreise($cursor: String) {
    nodes: hwIcQxPreisPages(after: $cursor) {
      edges {
        cursor
        node {
          ...qxPreisFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${QX_PREIS_FRAGMENT}
`
export const QX_ENTITY_QUERY = gql`
  query QxPreis($nr: ID!) {
    entity: hwIcQxPreis(designator: $nr) {
      ...qxPreisFragment
    }
  }
  ${QX_PREIS_FRAGMENT}
`

export const QX_EDIT_MUTATION = gql`
  mutation QxPreisAendern($designator: ID!, $input: HwIcQxPreisPatchInput!, $oldRev: ID!) {
    patchInfo: hwIcQxPreisPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...qxPreisFragment
      }
      new {
        ...qxPreisFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${QX_PREIS_FRAGMENT}
`

export const LAGERLISTE_QUERY = gql`
  query ItemPreisListe($cursor: String) {
    nodes: h3ItemPages(after: $cursor, filter: { ist_aktiv: { eq: true } }) {
      edges {
        cursor
        node {
          id
          designator
          name
          forDesignator
          ean
          zukunft {
            buchbestand
            frei_ab
            frei_max
            freie_menge
            naechster_zulauf
            zukuenftiger_zulauf
          }
          preisResolved {
            bartnr
            fh
            uvp
          }
          skuSum {
            mngEx
            mngPal
          }
          produkt {
            kategorie
          }
          pim {
            einstufung
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export const AUFTRAGSENTWURFALLE_LIST_QUERY = gql`
  query AuftragsentwurfErledigtListQuery($cursor: String) {
    nodes: h3AuftragsEntwurfPages(
      after: $cursor
      filter: { erledigt: { eq: true } }
      sort: { fields: [updated_at], order: DESC }
    ) {
      edges {
        cursor
        node {
          ...auftragsEntwurfFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${AUFTRAGSENTWURF_FRAGMENT}
` // Liste aller genehmigungsbedürftigen Aufträge
export const LIST_UNGENEHMIGT_QUERY = gql`
  query GenehmigungsAuftraege($cursor: String) {
    nodes: hwAuftragPages(after: $cursor, filter: { status: { gt: "200", lt: "250" } }) {
      edges {
        cursor
        node {
          ...auftragFragment
          kunde {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${AUFTRAG_FRAGMENT}
`
export const TEMPLATECONFIG_LIST_QUERY = gql`
  query ConfigTemplateList($cursor: String) {
    nodes: h3ConfigTemplatePages(after: $cursor) {
      edges {
        cursor
        node {
          ...configTemplateFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${TEMPLATECONFIG_FRAGMENT}
`
export const TEMPLATECONFIG_ENTITY_QUERY = gql`
  query ConfigTemplate($nr: ID!) {
    entity: h3ConfigTemplate(designator: $nr) {
      id
      ...configTemplateFragment
    }
  }
  ${TEMPLATECONFIG_FRAGMENT}
`
export const TEMPLATECONFIG_EDIT_MUTATION = gql`
  mutation ConfigTemplateEditMutation($designator: ID!, $input: H3ConfigTemplatePatchInput!, $oldRev: ID!) {
    patchInfo: h3ConfigTemplatePatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...configTemplateFragment
      }
      new {
        ...configTemplateFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${TEMPLATECONFIG_FRAGMENT}
`
export const PREIS_LIST_QUERY = gql`
  query PreiseAuflisten($cursor: String) {
    nodes: hwPreisPages(after: $cursor, filter: { ist_aktiv: { eq: true } }) {
      edges {
        cursor
        node {
          ...preisLFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${PREIS_FRAGMENT}
`

export const PREIS_CREATE_MUTATION = gql`
  mutation PreisZufuegen($input: HwPreisInput!) {
    entity: hwPreisCreate(input: $input) {
      ...preisLFragment
    }
  }
  ${PREIS_FRAGMENT}
`

export const PREIS_EDIT_MUTATION = gql`
  mutation PreisAendern($designator: ID!, $input: HwPreisPatchInput!, $oldRev: ID!) {
    patchInfo: hwPreisPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...preisLFragment
      }
      new {
        ...preisLFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${PREIS_FRAGMENT}
`
